import React, { useEffect, useState } from 'react'
import Select from 'react-select'
import BootstrapTable from 'react-bootstrap-table-next'
import ToolkitProvider from 'react-bootstrap-table2-toolkit'
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Col,
  Label,
  Row,
} from 'reactstrap'
import DisputeDto from 'src/api-dtos/disputes/dispute.dto.interface'
import DisputeStatus from 'src/api-dtos/disputes/dispute-status.type'
import DisputeReason from 'src/api-dtos/disputes/dispute-reason.type'
import {
  supportFetchMerchantDisputes,
  supportMarkDisputeAutoDeducted,
} from 'src/helpers/lopay_api_helper'
import SupportMerchantApiDto from 'src/api-dtos/support/merchant/support-merchant-api.dto.interface'
import { priceFormat, buildStripeDashboardPaymentLink } from 'src/helpers/utils'
import moment from 'moment'
import ConfirmationModal from 'src/components/Common/ConfirmationModal/ConfirmationModal'

// Status label map
const disputeStatusLabelMap: Record<DisputeStatus, string> = {
  warning_needs_response: 'Warning - Needs Response',
  warning_under_review: 'Warning - Under Review',
  warning_closed: 'Warning - Closed',
  needs_response: 'Needs Response',
  under_review: 'Under Review',
  charge_refunded: 'Charge Refunded',
  won: 'Won',
  lost: 'Lost',
}

// Reason label map
const disputeReasonLabelMap: Record<DisputeReason, string> = {
  bank_cannot_process: 'Bank Cannot Process',
  check_returned: 'Check Returned',
  credit_not_processed: 'Credit Not Processed',
  customer_initiated: 'Customer Initiated',
  debit_not_authorized: 'Debit Not Authorized',
  duplicate: 'Duplicate',
  fraudulent: 'Fraudulent',
  general: 'General',
  incorrect_account_details: 'Incorrect Account Details',
  insufficient_funds: 'Insufficient Funds',
  product_not_received: 'Product Not Received',
  product_unacceptable: 'Product Unacceptable',
  subscription_canceled: 'Subscription Canceled',
  unrecognized: 'Unrecognized',
}

// Disputes table columns - note: formatter functions are defined in the component
const disputesColumns = [
  {
    dataField: 'id',
    text: 'ID',
  },
  {
    dataField: 'status',
    text: 'Status',
  },
  {
    dataField: 'reason',
    text: 'Reason',
  },
  {
    dataField: 'amount',
    text: 'Amount',
  },
  {
    dataField: 'autoDeducted',
    text: 'Auto Deducted',
  },
  {
    dataField: 'evidenceDueBy',
    text: 'Evidence Due By',
  },
  {
    dataField: 'createdAt',
    text: 'Created',
  },
  {
    dataField: 'actions',
    text: 'Actions',
  },
]

type SelectOption = { value: DisputeStatus; label: string }

const availableDisputeStatusFilters: SelectOption[] = Object.entries(
  disputeStatusLabelMap,
).map(([value, label]) => ({
  value: value as DisputeStatus,
  label,
}))

type IComponentProps = {
  merchant: SupportMerchantApiDto | undefined
}

const SupportMerchantDisputes: React.FC<IComponentProps> = ({ merchant }) => {
  const [disputes, setDisputes] = useState<DisputeDto[]>([])
  const [selectedDisputeStatusFilters, setSelectedDisputeStatusFilters] =
    useState<SelectOption[]>(availableDisputeStatusFilters)

  // Cursor-based pagination state
  const [pageSize] = useState(20)
  const [nextOffsetId, setNextOffsetId] = useState<string | undefined>(
    undefined,
  )
  const [offsetIdHistory, setOffsetIdHistory] = useState<
    (string | undefined)[]
  >([])
  const [totalCount, setTotalCount] = useState<number>(0)
  const [currentOffsetId, setCurrentOffsetId] = useState<string | undefined>(
    undefined,
  )

  // Confirmation modal state
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false)
  const [selectedDispute, setSelectedDispute] = useState<DisputeDto | null>(
    null,
  )

  const disputesList = (): {
    id: string
    status: string
    reason: string
    amount: string
    autoDeducted: string
    evidenceDueBy: string
    createdAt: string
    actions: string
  }[] => {
    return disputes?.map((dispute) => ({
      id: dispute.id,
      status: disputeStatusLabelMap[dispute.status],
      reason: disputeReasonLabelMap[dispute.reason],
      amount: priceFormat(dispute.amount),
      autoDeducted: dispute.amountAutoDeducted ? 'Yes' : 'No',
      evidenceDueBy: dispute.evidenceDueBy
        ? moment(dispute.evidenceDueBy).local().format('DD-MM-YY HH:mm')
        : 'N/A',
      createdAt: moment(dispute.createdAt).local().format('DD-MM-YY HH:mm'),
      actions: '',
    }))
  }

  const loadMerchantDisputes = React.useCallback(
    async (offsetId?: string) => {
      if (!merchant) {
        return
      }

      const statuses = selectedDisputeStatusFilters.map(
        (option) => option.value,
      )
      const response = await supportFetchMerchantDisputes({
        merchantId: merchant.id,
        statuses: statuses.length > 0 ? statuses : undefined,
        pageSize,
        offsetId,
      })
      setDisputes(response.disputes)
      setNextOffsetId(response.offsetId)
      setTotalCount(response.count)
      setCurrentOffsetId(offsetId)
    },
    [merchant, selectedDisputeStatusFilters, pageSize],
  )

  const handleNextPage = () => {
    if (nextOffsetId) {
      setOffsetIdHistory([...offsetIdHistory, currentOffsetId])
      loadMerchantDisputes(nextOffsetId)
    }
  }

  const handlePreviousPage = () => {
    if (offsetIdHistory.length > 0) {
      const newHistory = [...offsetIdHistory]
      const previousOffsetId = newHistory.pop()
      setOffsetIdHistory(newHistory)
      loadMerchantDisputes(previousOffsetId)
    }
  }

  const resetPagination = () => {
    setOffsetIdHistory([])
    setCurrentOffsetId(undefined)
    setNextOffsetId(undefined)
    loadMerchantDisputes(undefined)
  }

  const toggleConfirmModal = (dispute?: DisputeDto) => {
    setSelectedDispute(dispute || null)
    setIsConfirmModalOpen((prev) => !prev)
  }

  const handleMarkAutoDeducted = async () => {
    if (!merchant || !selectedDispute) {
      return
    }

    try {
      await supportMarkDisputeAutoDeducted({
        merchantId: merchant.id,
        disputeId: selectedDispute.id,
      })
      // Reload disputes to show updated status
      await loadMerchantDisputes(currentOffsetId)
    } catch (error) {
      console.error('Failed to mark dispute as auto-deducted:', error)
    }
  }

  // Define columns with formatter inside component to access state and handlers
  const disputesColumnsWithFormatters = disputesColumns.map((col) => {
    if (col.dataField === 'autoDeducted') {
      return {
        ...col,
        formatter: (cell: string, row: { id: string }) => {
          const dispute = disputes.find((d) => d.id === row.id)
          if (!dispute) return cell

          if (dispute.amountAutoDeducted) {
            return <span>Yes</span>
          }

          return (
            <button
              type="button"
              className="btn btn-sm btn-warning"
              onClick={(e) => {
                e.preventDefault()
                toggleConfirmModal(dispute)
              }}
            >
              Mark Auto-Deducted
            </button>
          )
        },
      }
    }
    if (col.dataField === 'actions') {
      return {
        ...col,
        formatter: (cell: string, row: { id: string }) => {
          const dispute = disputes.find((d) => d.id === row.id)
          if (!dispute || !dispute.providerPaymentId) return <></>

          return (
            <a
              href={buildStripeDashboardPaymentLink(dispute.providerPaymentId)}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-sm btn-primary"
            >
              View Payment
            </a>
          )
        },
      }
    }
    return col
  })

  useEffect(() => {
    if (!merchant) {
      return
    }

    resetPagination()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchant, selectedDisputeStatusFilters])

  return (
    <Card>
      <CardHeader>
        <CardTitle className="h4">Disputes</CardTitle>
      </CardHeader>
      <CardBody>
        <Label htmlFor="disputeStatusFilter">Filter by dispute status</Label>
        <Select
          value={selectedDisputeStatusFilters}
          options={availableDisputeStatusFilters}
          onChange={(values: SelectOption[]) => {
            setSelectedDisputeStatusFilters(values)
          }}
          isMulti
        />
        <div className="mt-3">
          <ToolkitProvider
            keyField="id"
            columns={disputesColumnsWithFormatters}
            data={disputesList()}
            search
          >
            {(toolkitProps) => (
              <React.Fragment>
                <Row>
                  <Col xl="12">
                    <div className="table-responsive">
                      <BootstrapTable
                        bordered={false}
                        striped={false}
                        defaultSorted={[
                          {
                            dataField: 'createdAt',
                            order: 'desc',
                          },
                        ]}
                        classes={'table align-middle table-nowrap'}
                        headerWrapperClasses={'thead-light'}
                        {...toolkitProps.baseProps}
                      />
                    </div>
                  </Col>
                </Row>

                <Row className="align-items-md-center mt-3">
                  <Col className="d-flex justify-content-between align-items-center">
                    <div>
                      Showing {disputes.length} of {totalCount} disputes
                    </div>
                    <div className="d-flex gap-2">
                      <Button
                        color="primary"
                        onClick={handlePreviousPage}
                        disabled={offsetIdHistory.length === 0}
                      >
                        Previous
                      </Button>
                      <Button
                        color="primary"
                        onClick={handleNextPage}
                        disabled={!nextOffsetId}
                      >
                        Next
                      </Button>
                    </div>
                  </Col>
                </Row>
              </React.Fragment>
            )}
          </ToolkitProvider>
        </div>
      </CardBody>

      <ConfirmationModal
        isOpen={isConfirmModalOpen}
        toggle={() => toggleConfirmModal()}
        onConfirm={handleMarkAutoDeducted}
      >
        <p>
          Are you sure you want to mark this dispute as auto-deducted? This will
          prevent the system from attempting to deduct the disputed amount from
          this merchant. If there are other non-deducted disputes, those
          disputes will be next in line to be deducted.
        </p>

        <p>
          <strong>
            You are now responsible for manually making this deduction and
            notifying the merchant.
          </strong>
        </p>
      </ConfirmationModal>
    </Card>
  )
}

export default SupportMerchantDisputes
